import { r as registerInstance, h, H as Host } from './index-662ec80e.js';
import { p as propCheck, r as runWatchers, h as hasPropError, d as displayPropError } from './propCheck-e7571e3e.js';
import { d as vOneOfI, S as vOneOf, a1 as vUndefOr, s as vTransactionHash, e as vPattern, a as vString, Q as vBoolean } from './validation-02b8d01d.js';
import { t as tuple } from './types-6f6b2fba.js';
import { e as ethereumNetworks } from './ethereum-81afa4d8.js';
import { c as copyToClipboard } from './browser-202fc7f4.js';
import { e as explorerLink } from './explorer-link-ff43ae28.js';
import './i18n-939e2dee.js';

const linkTypes = tuple('wallet', 'onchainid', 'transaction', 'token');

const dsExplorerLinkCss = "ds-explorer-link{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;overflow:visible !important}ds-explorer-link a{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;padding:calc(var(--gw) * 0.3) calc(var(--gw) * 0.5);background-color:var(--blue-50);color:var(--blue-700);letter-spacing:0.008rem;border-radius:var(--radius);font-size:0.79012346rem;line-height:1.3rem;width:inherit;white-space:nowrap;overflow-x:hidden;text-overflow:ellipsis;line-height:1rem}ds-explorer-link[type='onchainid'] a{background-color:var(--purple-50);color:var(--purple-600)}ds-explorer-link a:active,ds-explorer-link a:hover{text-decoration:underline}ds-explorer-link a:active,ds-explorer-link a:hover,ds-explorer-link a:focus,ds-explorer-link a:visited{color:var(--blue-700)}ds-explorer-link:not([width='']) a span:first-of-type{display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}ds-explorer-link:not([id-connected]) a:active,ds-explorer-link:not([id-connected]) a:hover,ds-explorer-link:not([id-connected]) a:focus,ds-explorer-link:not([id-connected]) a:visited{color:var(--coolGrey-600)}ds-explorer-link[type='onchainid'] a:active,ds-explorer-link[type='onchainid'] a:hover,ds-explorer-link[type='onchainid'] a:focus,ds-explorer-link[type='onchainid'] a:visited{color:var(--purple-600)}ds-explorer-link ds-tooltip{cursor:pointer}ds-explorer-link ds-icon[name='copy']{color:var(--grey-900)}ds-explorer-link ds-icon[name='copy']:hover{color:var(--black)}";

const DsExplorerLink = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.propError = {};
    this.type = 'wallet';
    this.network = 'kovan';
    this.address = undefined;
    this.width = undefined;
    this.isCopyable = true;
    this.isTokenTransfer = false;
    this.idConnected = true;
    this.isCopied = false;
  }
  watchType() {
    /* istanbul ignore if */
    if (!propCheck(this, 'type', linkTypes.join(' | '), vOneOf(linkTypes)))
      return;
  }
  watchNetwork() {
    /* istanbul ignore if */
    if (!propCheck(this, 'network', ethereumNetworks.join(' | '), vOneOfI(ethereumNetworks)))
      return;
  }
  watchAddress() {
    /* istanbul ignore if */
    if (!propCheck(this, 'address', 'string', vUndefOr(v => vTransactionHash(v) &&
      vPattern(/^(?:1|2|3|BC1|M|N)[A-Z0-9]{26,39}$/i)(v) && // 27 to 39 alphanumeric characters, with each one beginning with 1, 2, 3, m, n or bc1
      vPattern(/^[A-F0-9]{64}$/i)(v) // BTC is 64 characters and hexadecimal. (It doesn't start w/ 0x as ETH)
    )))
      return;
  }
  watchWidth() {
    /* istanbul ignore if */
    if (!propCheck(this, 'width', 'string ', vUndefOr(vString)))
      return;
  }
  watchIsCopyable() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isCopyable', 'boolean', vBoolean))
      return;
  }
  watchIsTokenTransfer() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isTokenTransfer', 'boolean', vBoolean))
      return;
  }
  watchIdConnected() {
    /* istanbul ignore if */
    if (!propCheck(this, 'idConnected', 'boolean', vBoolean))
      return;
  }
  href() {
    return explorerLink({
      network: this.network,
      address: this.address,
      type: this.type,
      isTokenTransfer: this.isTokenTransfer
    });
  }
  icon() {
    switch (this.type) {
      case 'wallet':
        return 'wallet';
      case 'onchainid':
        return 'onchainid';
      case 'token':
        return 'token';
      case 'transaction':
        return 'transfer';
    }
  }
  async copy() {
    await copyToClipboard(this.address);
    this.isCopied = true;
    setTimeout(() => {
      this.isCopied = false;
    }, 2000);
  }
  getCroppedWidth() {
    if (!this.width)
      return 'auto';
    const remUnit = parseInt(getComputedStyle(document.documentElement).fontSize);
    if (this.width.includes('rem')) {
      return `${(parseInt(this.width) * remUnit) - 52}px`;
    }
    else {
      return `${parseInt(this.width) - 52}px`;
    }
  }
  connectedCallback() {
    runWatchers(this);
  }
  render() {
    if (hasPropError(this))
      return displayPropError(this);
    if ('0x0000000000000000000000000000000000000000' === this.address || !this.address) {
      return h(Host, null, " - ");
    }
    else {
      return h(Host, null, h("a", { rel: "noopener noreferrer", target: "_blank", href: this.href() }, h("ds-icon", { name: this.icon(), class: "_m:right(2)", size: "10px", color: "none" }), h("span", { style: { width: this.getCroppedWidth() } }, this.address.slice(0, this.address.length - 3)), h("span", null, this.address.slice(-3))), this.isCopyable &&
        h("ds-tooltip", { text: this.isCopied ? 'Copied' : 'Copy', size: "xs", onClick: () => this.copy() }, h("ds-icon", { name: "copy", class: "_m:left(2)", size: "11px" })));
    }
  }
  static get watchers() { return {
    "type": ["watchType"],
    "network": ["watchNetwork"],
    "address": ["watchAddress"],
    "width": ["watchWidth"],
    "isCopyable": ["watchIsCopyable"],
    "isTokenTransfer": ["watchIsTokenTransfer"],
    "idConnected": ["watchIdConnected"]
  }; }
};
DsExplorerLink.style = dsExplorerLinkCss;

export { DsExplorerLink as ds_explorer_link };
